import React, { useState, useEffect } from 'react';
import InfiniteSelect from '../../../../../components/InfiniteSelect';
import axios from '../../../../../config/axios';
import { FormGroup, Label } from '../style';
import ScriptPreviewTrigger from './ScriptPreview';

interface SteelOrdersInfiniteSelectOption {
  id: number;
  name: string;
  stages?: string[];
  materialsCount?: number;
  ncmId?: number;
  markup?: number | null;
  // Mesmo shape do Option.prices do InfiniteSelect base (preço trafega como string).
  prices?: { id?: number | string; price: string; unitOfMeasurement?: { id: number; name: string } }[];
}

interface SteelOrdersInfiniteSelectProps {
  items: SteelOrdersInfiniteSelectOption[];
  setItems: React.Dispatch<React.SetStateAction<SteelOrdersInfiniteSelectOption[]>>;
  endpoint: string;
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  isDisabled?: boolean;
  additionalParams?: Record<string, any>;
  customOptions?: SteelOrdersInfiniteSelectOption[];
  onCleared?: () => void;
}

const SteelOrdersInfiniteSelect: React.FC<SteelOrdersInfiniteSelectProps> = ({
  endpoint,
  items,
  setItems,
  label,
  placeholder,
  searchPlaceholder,
  isDisabled,
  additionalParams,
  ...props
}) => {
  const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`;
  
  const fetchFunction = async (limit: number, offset: number, search?: string) => {
    if (props.customOptions) return props.customOptions;
    try {
      const params: any = { limit };
      // Termo numérico É busca por texto: o backend já casa id E nome no mesmo OR, então mandar
      // `id` desligava metade da busca (digitar `347` trazia o material de id 347, não a liga).
      // `scripts` é a exceção: o controller aceita só `id`/`productId`, não tem `search`.
      const termo = typeof search === 'string' ? search.trim() : '';
      if (termo) {
        if (endpoint === 'scripts' && /^\d+$/.test(termo)) {
          params.id = Number(termo);
        } else {
          params.search = termo;
        }
      }

      const page = Math.floor(offset / limit) + 1;
      params.page = page;

      if (additionalParams && typeof additionalParams === 'object') {
        Object.assign(params, additionalParams);
        if (endpoint === 'scripts' && (additionalParams as any).productId) {
          const pid = (additionalParams as any).productId;
          params.product_id = pid;
          params.finalProductId = pid;
          params.final_product_id = pid;
          params.product = pid;
        }
      }

      if (endpoint === 'scripts') {
        const hasProductParam = params.productId || params.product_id || params.finalProductId || params.final_product_id || params.product;
        if (!hasProductParam) {
          return [];
        }
      }

      if (endpoint === 'scripts') {
        params.offset = offset;
        delete params.page;
      }

      const response = await axios.get(`${API_BASE_URL}/select-in-order/${endpoint}`, { params });
      let data = [];
      if (response.data.services) {
        data = response.data.services;
      } else if (response.data.resources) {
        data = response.data.resources;
      } else if (response.data.suppliers) {
        data = response.data.suppliers;
      } else if (response.data.products) {
        data = response.data.products;
      } else if (response.data.stocks) {
        data = (response.data.stocks || []).map((s: any) => ({
          ...s,
          name: s.description || s.name || '',
        }));
      } else if (response.data.scripts) {
        data = response.data.scripts;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else {
        data = [];
      }

      if (endpoint === 'materials' && Array.isArray(data)) {
        data = data.map((m: any) => ({
          ...m,
          density: m?.density ?? null,
        }));
      }

      if (endpoint === 'scripts') {
        const mapped = Array.isArray(data)
          ? data.map((s: any, idx: number) => {
              if (typeof s === 'string') {
                return { id: idx + 1, name: s };
              }
              return {
                id: s?.id ?? idx + 1,
                name: String(s?.name ?? ''),
                stages: Array.isArray(s?.stages) ? s.stages : [],
                materialsCount: typeof s?.materialsCount === 'number' ? s.materialsCount : 0,
              };
            })
          : [];
        return mapped;
      }

      const formattedData = Array.isArray(data) ? data.map((item: any) => ({
        id: item.id,
        name: item.name,
        ...item
      })) : [];
      
      return formattedData;
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      return [];
    }
  };

  const handleSelect = (option: any) => {
    if (option) {
      setItems([option]);
    } else {
      setItems([]);
      if (props.onCleared) {
        try { props.onCleared(); } catch (e){
        // Ignore errors
        }
      }
    }
  };

  const [openScriptId, setOpenScriptId] = useState<number | null>(null);

  const selectedValueRaw = items.length > 0 ? items[0] : null;
  const selectedValue = isDisabled ? null : selectedValueRaw;

  const getFieldsConfig = () => {
    if (endpoint === 'scripts') {
      return [
        {
          key: 'preview',
          label: '',
          // renderField descarta valor vazio: option.id está sempre presente, stages nem sempre.
          getValue: (option: any) => option.id,
          render: (_value: any, option: any) => (
            <ScriptPreviewTrigger
              option={option}
              isOpen={openScriptId === option.id}
              onToggle={setOpenScriptId}
            />
          ),
        },
      ];
    }

    if (endpoint === 'stocks') {
      return [
        {
          key: 'quantity',
          label: 'Qtd. (pç)',
          getValue: (option: any) => option.quantity,
          render: (value: any) => value ? `${parseFloat(value).toFixed(2)}` : '',
          condition: (option: any) => option.quantity !== undefined && option.quantity !== null
        },
        {
          key: 'totalWeight',
          label: 'Peso (kg)',
          getValue: (option: any) => option.totalWeight,
          render: (value: any) => value ? `${parseFloat(value).toFixed(2)}` : '',
          condition: (option: any) => option.totalWeight !== undefined && option.totalWeight !== null
        },
        {
          key: 'format',
          label: 'Formato',
          getValue: (option: any) => option.format,
          condition: (option: any) => option.format
        },
        {
          key: 'fabrication',
          label: 'Fabricação',
          getValue: (option: any) => option.fabrication,
          condition: (option: any) => option.fabrication
        },
        {
          key: 'heatTreatment',
          label: 'Trat. Térmico',
          getValue: (option: any) => option.heatTreatment,
          condition: (option: any) => option.heatTreatment
        },
        {
          key: 'supplier',
          label: 'Fornecedor',
          getValue: (option: any) => option.supplier,
          condition: (option: any) => option.supplier
        }
      ];
    }
    return [];
  };

  return (
    <FormGroup>
      {label && <Label>{label}</Label>}
      <InfiniteSelect
        value={selectedValue}
        onSelect={handleSelect}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        fetchFunction={fetchFunction}
        disabled={isDisabled}
        mode="readonly"
        fieldsConfig={getFieldsConfig()}
        showDefaultFields={true}
        getOptionLabel={(opt: any) => {
          if (endpoint === 'materials') {
            return opt.name || '';
          }
          if (endpoint === 'stocks') {
            return opt.description || opt.name || '';
          }
          return opt.name;
        }}
      />
    </FormGroup>
  );
};

export default SteelOrdersInfiniteSelect;
